/**
 * Category constants for the public map application.
 * These are UI helper data, not dynamic API data.
 */

// Category → Lucide icon name mapping (used for PlaceDetails header & search items)
export const categoryIconNames: Record<string, string> = {
  // Food & Drink
  restaurant: 'utensils',
  fast_food: 'hamburger',
  cafe: 'coffee',
  bar: 'beer',
  pub: 'beer',
  bakery: 'croissant',
  ice_cream: 'ice-cream-cone',
  nightclub: 'beer',
  biergarten: 'beer',
  food_court: 'utensils',
  // Finance
  bank: 'landmark',
  atm: 'credit-card',
  bureau_de_change: 'banknote',
  money_transfer: 'send',
  mobile_money_agent: 'smartphone',
  // Healthcare
  hospital: 'hospital',
  clinic: 'stethoscope',
  pharmacy: 'pill',
  doctors: 'stethoscope',
  dentist: 'smile',
  veterinary: 'paw-print',
  nursing_home: 'heart-pulse',
  // Education
  school: 'school',
  kindergarten: 'baby',
  college: 'graduation-cap',
  university: 'graduation-cap',
  library: 'library',
  language_school: 'school',
  music_school: 'school',
  // Transport
  bus_station: 'bus',
  bus_stop: 'bus',
  transit_stop: 'bus',
  airport: 'plane',
  aeroway: 'plane',
  natural: 'waves',
  water: 'waves',
  lake: 'waves',
  river: 'waves',
  country: 'globe',
  taxi: 'car',
  fuel: 'fuel',
  gas_station: 'fuel',
  parking: 'parking-circle',
  car_wash: 'car',
  car_repair: 'wrench',
  bicycle_rental: 'car',
  charging_station: 'fuel',
  ferry_terminal: 'navigation',
  // Accommodation
  hotel: 'hotel',
  guest_house: 'home',
  hostel: 'bed',
  motel: 'bed-double',
  apartment: 'building',
  // Shopping
  shopping: 'shopping-bag',
  supermarket: 'shopping-cart',
  convenience: 'store',
  kiosk: 'store',
  marketplace: 'store',
  mall: 'shopping-bag',
  department_store: 'shopping-bag',
  clothes: 'shirt',
  tailor: 'shirt',
  boutique: 'shirt',
  electronics: 'smartphone',
  mobile_phone: 'smartphone',
  computer: 'smartphone',
  hardware: 'wrench',
  furniture: 'sofa',
  hairdresser: 'store',
  beauty: 'store',
  books: 'book-open',
  // Government & Civic
  government: 'building-2',
  townhall: 'building-2',
  courthouse: 'scale',
  embassy: 'building-2',
  community_centre: 'building-2',
  police: 'shield',
  fire_station: 'flame',
  post_office: 'mail',
  prison: 'shield',
  // Religion
  religious: 'church',
  place_of_worship: 'church',
  church: 'church',
  mosque: 'church',
  // Leisure
  park: 'trees',
  playground: 'ferris-wheel',
  sports_centre: 'dumbbell',
  fitness_centre: 'dumbbell',
  swimming_pool: 'waves',
  stadium: 'trophy',
  beach_resort: 'umbrella',
  // Tourism
  museum: 'building-2',
  attraction: 'star',
  viewpoint: 'eye',
  artwork: 'palette',
  // Office
  company: 'building',
  insurance: 'building',
  ngo: 'building-2',
  association: 'building-2',
  // Services & Business
  shop: 'store',
  studio: 'camera',
  // Craft
  carpenter: 'wrench',
  electrician: 'wrench',
  photographer: 'camera',
  printing: 'printer',
  // Entertainment
  entertainment: 'clapperboard',
  casino: 'dice-5',
  internet_cafe: 'wifi',
  // Accommodation
  dormitory: 'bed-double',
  // Transport
  station: 'train-front',
  // Services
  mortuary: 'building-2',
  barber: 'scissors',
  salon: 'scissors',
  co_working: 'laptop',
  cultural_centre: 'palette',
  event_venue: 'calendar',
  train_station: 'train-front',
  transit_station: 'train-front',
  startup: 'rocket',
  security: 'shield-check',
  advertising: 'megaphone',
  printing_shop: 'printer',
  sawmill: 'tree-pine',
  recycling: 'recycle',
  cinema: 'clapperboard',
  theatre: 'drama',
  arts_centre: 'palette',
  // Historic
  monument: 'building-2',
  castle: 'building-2',
  memorial: 'building-2',
  landmark: 'building-2',
  // Transport (top-level)
  transport: 'bus',
  // Accommodation (top-level)
  accommodation: 'hotel',
  // Generic
  building: 'building',
  address: 'home',
  street: 'route',
  road: 'route',
  poi: 'map-pin',
  place: 'map-pin',
  amenity: 'map-pin',
  other: 'map-pin',
};

// Comprehensive emoji icons for all DB categories
export const categoryIcons: Record<string, string> = {
  // Food & Drink
  restaurant: '🍽️',
  fast_food: '🍔',
  cafe: '☕',
  bar: '🍸',
  pub: '🍺',
  bakery: '🥐',
  ice_cream: '🍦',
  nightclub: '🎵',
  biergarten: '🍺',
  food_court: '🍽️',
  // Finance
  bank: '🏦',
  atm: '💳',
  bureau_de_change: '💱',
  money_transfer: '💸',
  mobile_money_agent: '📱',
  microfinance: '🏦',
  // Healthcare
  hospital: '🏥',
  clinic: '🩺',
  pharmacy: '💊',
  doctors: '🩺',
  dentist: '🦷',
  veterinary: '🐾',
  nursing_home: '🏥',
  chemists: '💊',
  // Education
  school: '🎓',
  kindergarten: '👶',
  college: '🎓',
  university: '🎓',
  library: '📚',
  language_school: '🎓',
  music_school: '�',
  training: '�',
  // Transport
  bus_station: '🚌',
  bus_stop: '🚌',
  taxi: '🚕',
  fuel: '⛽',
  gas_station: '⛽',
  parking: '🅿️',
  car_wash: '🚗',
  car_repair: '🔧',
  bicycle_rental: '🚲',
  charging_station: '🔌',
  ferry_terminal: '⛴️',
  motorcycle_parking: '🏍️',
  // Accommodation
  hotel: '🏨',
  guest_house: '🏠',
  hostel: '🛏️',
  motel: '🛏️',
  apartment: '🏢',
  chalet: '🏔️',
  camp_site: '⛺',
  resort: '�️',
  // Shopping
  shopping: '🛍️',
  supermarket: '🛒',
  convenience: '🏪',
  kiosk: '🏪',
  marketplace: '🏪',
  mall: '🛍️',
  department_store: '🛍️',
  clothes: '👕',
  tailor: '👕',
  boutique: '👗',
  electronics: '📱',
  mobile_phone: '📱',
  computer: '💻',
  hardware: '🔨',
  furniture: '🛋️',
  hairdresser: '💇',
  beauty: '💄',
  cosmetics: '💄',
  books: '📖',
  shoes: '👟',
  jewelry: '💍',
  butcher: '🥩',
  greengrocer: '🥬',
  beverages: '🥤',
  bakery_shop: '🥐',
  car: '🚗',
  car_parts: '🔧',
  laundry: '👔',
  copyshop: '🖨️',
  chemist: '💊',
  optician: '👓',
  gift: '🎁',
  stationery: '📝',
  general: '🏪',
  variety_store: '🏪',
  // Government & Civic
  government: '🏛️',
  townhall: '🏛️',
  courthouse: '⚖️',
  embassy: '🏛️',
  diplomatic: '🏛️',
  community_centre: '🏛️',
  police: '🚔',
  fire_station: '🚒',
  post_office: '📮',
  prison: '🔒',
  customs: '🛃',
  // Religion
  religious: '⛪',
  place_of_worship: '⛪',
  church: '⛪',
  mosque: '🕌',
  // Leisure & Sports
  park: '🌳',
  playground: '🎠',
  sports_centre: '🏟️',
  fitness_centre: '💪',
  swimming_pool: '🏊',
  stadium: '🏟️',
  pitch: '⚽',
  beach_resort: '🏖️',
  water_park: '🏖️',
  garden: '🌷',
  golf_course: '⛳',
  // Tourism & Culture
  museum: '🏛️',
  gallery: '🎨',
  attraction: '⭐',
  viewpoint: '🔭',
  artwork: '�',
  information: 'ℹ️',
  zoo: '🦁',
  theme_park: '🎢',
  // Entertainment
  entertainment: '🎉',
  casino: '🎰',
  cinema: '🎬',
  theatre: '🎭',
  arts_centre: '🎨',
  amusement_arcade: '🕹️',
  // Office
  company: '🏢',
  insurance: '🏢',
  financial: '🏦',
  telecommunication: '📡',
  ngo: '🏛️',
  association: '🏛️',
  educational_institution: '🎓',
  research: '🔬',
  it: '💻',
  lawyer: '⚖️',
  estate_agent: '🏠',
  travel_agent: '✈️',
  political_party: '🏛️',
  // Services & Business
  shop: '🏪',
  studio: '📷',
  // Craft
  carpenter: '🔨',
  electrician: '⚡',
  photographer: '📷',
  printing: '🖨️',
  electronics_repair: '🔧',
  metal_construction: '🔨',
  grinding_mill: '🏭',
  sawmill: '🪵',
  dressmaker: '👗',
  shoemaker: '👞',
  blacksmith: '🔨',
  // Historic
  monument: '🏛️',
  memorial: '🏛️',
  castle: '🏰',
  ruins: '🏚️',
  landmark: '🏛️',
  // Internet & Tech
  internet_cafe: '💻',
  co_working: '💻',
  startup: '🚀',
  // Accommodation
  dormitory: '🛏️',
  // Transport
  station: '🚆',
  train_station: '🚆',
  transit_station: '🚆',
  // Services
  mortuary: '🏥',
  barber: '✂️',
  salon: '💇',
  cultural_centre: '🎨',
  event_venue: '📅',
  advertising: '📣',
  printing_shop: '🖨️',
  recycling: '♻️',
  security: '🛡️',
  // Water & Sanitation
  water_point: '💧',
  drinking_water: '💧',
  toilets: '🚻',
  waste_basket: '🗑️',
  waste_disposal: '🗑️',
  // Transport (additional)
  transport: '✈️',
  // Generic
  building: '🏢',
  address: '🏠',
  place: '📍',
  poi: '📍',
  amenity: '📍',
  other: '📍',
  yes: '📍',
};

// Category labels - comprehensive mapping of POI types to human-readable labels
export const categoryLabels: Record<string, string> = {
  // Food & Drink
  restaurant: 'Restaurant',
  fast_food: 'Fast Food',
  cafe: 'Café',
  bar: 'Bar',
  pub: 'Pub',
  bakery: 'Bakery',
  ice_cream: 'Ice Cream',
  nightclub: 'Nightclub',
  biergarten: 'Beer Garden',
  food_court: 'Food Court',
  // Finance
  bank: 'Bank',
  atm: 'ATM',
  bureau_de_change: 'Currency Exchange',
  money_transfer: 'Money Transfer',
  mobile_money_agent: 'Mobile Money',
  microfinance: 'Microfinance',
  // Healthcare
  hospital: 'Hospital',
  clinic: 'Clinic',
  pharmacy: 'Pharmacy',
  doctors: 'Doctor',
  dentist: 'Dentist',
  veterinary: 'Veterinary',
  nursing_home: 'Nursing Home',
  chemists: 'Chemist',
  // Education
  school: 'School',
  kindergarten: 'Kindergarten',
  college: 'College',
  university: 'University',
  library: 'Library',
  language_school: 'Language School',
  music_school: 'Music School',
  training: 'Training Centre',
  // Transport
  bus_station: 'Bus Station',
  bus_stop: 'Bus Stop',
  transit_stop: 'Transit Stop',
  airport: 'Airport',
  aeroway: 'Aeroway',
  natural: 'Natural Feature',
  water: 'Water Body',
  lake: 'Lake',
  river: 'River',
  country: 'Country',
  taxi: 'Taxi Stand',
  fuel: 'Fuel Station',
  gas_station: 'Fuel Station',
  parking: 'Parking',
  car_wash: 'Car Wash',
  car_repair: 'Auto Repair',
  bicycle_rental: 'Bike Rental',
  charging_station: 'Charging Station',
  ferry_terminal: 'Ferry Terminal',
  motorcycle_parking: 'Motorcycle Parking',
  // Accommodation
  hotel: 'Hotel',
  guest_house: 'Guest House',
  hostel: 'Hostel',
  motel: 'Motel',
  apartment: 'Apartment',
  chalet: 'Chalet',
  camp_site: 'Camping',
  resort: 'Resort',
  // Shopping
  shopping: 'Shopping',
  supermarket: 'Supermarket',
  convenience: 'Convenience Store',
  kiosk: 'Kiosk',
  marketplace: 'Market',
  mall: 'Shopping Mall',
  department_store: 'Department Store',
  clothes: 'Clothing Store',
  tailor: 'Tailor',
  boutique: 'Boutique',
  electronics: 'Electronics',
  mobile_phone: 'Mobile Phone',
  computer: 'Computer Store',
  hardware: 'Hardware Store',
  furniture: 'Furniture',
  hairdresser: 'Hairdresser',
  beauty: 'Beauty Salon',
  cosmetics: 'Cosmetics',
  books: 'Bookshop',
  shoes: 'Shoe Store',
  jewelry: 'Jewellery',
  butcher: 'Butcher',
  greengrocer: 'Greengrocer',
  beverages: 'Beverages',
  bakery_shop: 'Bakery',
  car: 'Car Dealer',
  car_parts: 'Car Parts',
  laundry: 'Laundry',
  copyshop: 'Copy Shop',
  chemist: 'Chemist',
  optician: 'Optician',
  gift: 'Gift Shop',
  stationery: 'Stationery',
  general: 'General Store',
  variety_store: 'Variety Store',
  // Government & Civic
  government: 'Government',
  townhall: 'Town Hall',
  courthouse: 'Courthouse',
  embassy: 'Embassy',
  diplomatic: 'Embassy',
  community_centre: 'Community Centre',
  police: 'Police Station',
  fire_station: 'Fire Station',
  post_office: 'Post Office',
  prison: 'Prison',
  customs: 'Customs',
  // Religion
  religious: 'Place of Worship',
  place_of_worship: 'Place of Worship',
  church: 'Church',
  mosque: 'Mosque',
  // Leisure & Sports
  park: 'Park',
  playground: 'Playground',
  sports_centre: 'Sports Centre',
  fitness_centre: 'Fitness Centre',
  swimming_pool: 'Swimming Pool',
  stadium: 'Stadium',
  pitch: 'Sports Field',
  beach_resort: 'Beach Resort',
  water_park: 'Water Park',
  garden: 'Garden',
  golf_course: 'Golf Course',
  // Tourism & Culture
  museum: 'Museum',
  gallery: 'Art Gallery',
  attraction: 'Attraction',
  viewpoint: 'Viewpoint',
  artwork: 'Public Art',
  information: 'Information',
  zoo: 'Zoo',
  theme_park: 'Theme Park',
  // Entertainment
  entertainment: 'Entertainment',
  cinema: 'Cinema',
  theatre: 'Theatre',
  arts_centre: 'Arts Centre',
  amusement_arcade: 'Arcade',
  // Office
  company: 'Company',
  insurance: 'Insurance',
  financial: 'Financial Services',
  telecommunication: 'Telecom',
  ngo: 'NGO',
  association: 'Association',
  educational_institution: 'Educational Inst.',
  research: 'Research',
  it: 'IT Company',
  lawyer: 'Lawyer',
  estate_agent: 'Estate Agent',
  travel_agent: 'Travel Agency',
  political_party: 'Political Party',
  // Services & Business
  shop: 'Shop',
  studio: 'Studio',
  // Craft
  carpenter: 'Carpenter',
  electrician: 'Electrician',
  photographer: 'Photographer',
  printing: 'Printing Shop',
  electronics_repair: 'Electronics Repair',
  metal_construction: 'Metal Works',
  grinding_mill: 'Grinding Mill',
  sawmill: 'Sawmill',
  dressmaker: 'Dressmaker',
  shoemaker: 'Shoemaker',
  blacksmith: 'Blacksmith',
  // Entertainment (extra)
  casino: 'Casino',
  // Internet / Tech
  internet_cafe: 'Internet Café',
  co_working: 'Co-working Space',
  startup: 'Startup / Hub',
  // Accommodation
  dormitory: 'Dormitory',
  // Transport
  station: 'Station',
  train_station: 'Train Station',
  transit_station: 'Transit Station',
  // Services
  mortuary: 'Mortuary',
  barber: 'Barber Shop',
  salon: 'Salon',
  cultural_centre: 'Cultural Centre',
  event_venue: 'Event Venue',
  advertising: 'Advertising Agency',
  printing_shop: 'Printing Shop',
  security: 'Security Firm',
  recycling: 'Recycling Point',
  // Historic
  monument: 'Monument',
  memorial: 'Memorial',
  castle: 'Castle',
  ruins: 'Ruins',
  // Water & Sanitation
  water_point: 'Water Point',
  drinking_water: 'Drinking Water',
  toilets: 'Public Toilets',
  waste_basket: 'Waste Collection',
  waste_disposal: 'Waste Disposal',
  // Generic
  landmark: 'Landmark',
  building: 'Address',
  address: 'Address',
  street: 'Road',
  road: 'Road',
  place: 'Place',
  poi: 'Point of Interest',
  amenity: 'Amenity',
  other: 'Place',
  yes: 'Place',
};

// Quick action categories for home screen (fallback when API unavailable)
export const quickCategories = [
  { id: 'restaurant', icon: 'utensils', color: 'orange', label: 'Food' },
  { id: 'hospital', icon: 'hospital', color: 'red', label: 'Hospital' },
  { id: 'gas_station', icon: 'fuel', color: 'red', label: 'Fuel' },
  { id: 'shopping', icon: 'shopping-bag', color: 'pink', label: 'Shopping' },
  { id: 'bank', icon: 'landmark', color: 'blue', label: 'Bank' },
  { id: 'pharmacy', icon: 'pill', color: 'green', label: 'Pharmacy' },
];

// Get category icon (emoji) with fallback
export function getCategoryIcon(category: string): string {
  return categoryIcons[category.toLowerCase()] || categoryIcons.other;
}

// Get category Lucide icon name with fallback
export function getCategoryIconName(category: string): string {
  return categoryIconNames[category.toLowerCase()] || 'map-pin';
}

// Get category label with fallback
export function getCategoryLabel(category: string): string {
  return categoryLabels[category.toLowerCase()] || category;
}

/** Label for search/autocomplete rows — type wins for streets so OSM road types are not shown raw. */
export function getSearchResultCategoryLabel(type?: string, category?: string | null): string {
  if (type === 'street') return categoryLabels.street;
  if (category) return getCategoryLabel(category);
  if (type) return getCategoryLabel(type);
  return categoryLabels.place;
}
